/**
 * SA-1090: how a staff note shows in the message pane. Pure.
 *
 * Every triaged message writes one note (the SA-717 audit trail, never
 * rewritten). An automatic decision's note is `level: info` and carries
 * `channel_metadata.triage.ledger_id`, the row it decided; it asks the
 * reader for nothing, so it renders as one muted line, the verdict, where
 * the amber box was. A note that asks for something (`attention`,
 * `warning`: proposed, undecided, failed, an SLA reminder, an engine fault,
 * a handoff summary), a push note with an empty triage block, or an older
 * note with no ledger id keeps its box.
 */
import type { InboxMessage } from '@/types/inbox'

/** The viewer's preference: today's boxes for every note, the verdicts as
 * markers, or the verdicts hidden. Boxes that ask for something always show. */
export type NoteMode = 'inline' | 'markers' | 'hidden'
export const NOTE_MODES: readonly NoteMode[] = ['inline', 'markers', 'hidden']
export const DEFAULT_NOTE_MODE: NoteMode = 'markers'

export const NOTE_MODE_LABEL: Record<NoteMode, string> = {
  inline: 'Full notes',
  markers: 'One line',
  hidden: 'Hidden',
}
export const NOTE_MODE_CONTROL_LABEL = 'AI notes'

export const STAFF_NOTE_PREFIX = '[staff note] '

export function isStaffNote(message: InboxMessage): boolean {
  return Boolean(message.channelMetadata?.staff_note) || (message.content ?? '').startsWith(STAFF_NOTE_PREFIX)
}

/** The note's text without its stored prefix. */
export function noteText(message: InboxMessage): string {
  return (message.content ?? '').replace(/^\[staff note\] /, '')
}

/** A verdict: an info-level note on a ledger row. */
export function isVerdictNote(message: InboxMessage): boolean {
  const meta = message.channelMetadata ?? {}
  if (!meta.staff_note || meta.level !== 'info') return false
  const triage = meta.triage as { ledger_id?: unknown } | null | undefined
  return typeof triage?.ledger_id === 'string' && triage.ledger_id.length > 0
}

export type NoteDisplay = 'box' | 'marker' | 'hidden'

/** How the pane shows a staff note under the viewer's preference. */
export function noteDisplay(message: InboxMessage, mode: NoteMode): NoteDisplay {
  if (!isVerdictNote(message)) return 'box'
  if (mode === 'inline') return 'box'
  return mode === 'hidden' ? 'hidden' : 'marker'
}

export interface MarkerParts {
  /** The verdict, the note's first sentence: `Classified as Operations (95%, model).` */
  headline: string
  /** The classifier's words, when the note quotes them. */
  rationale: string | null
  /** Everything after the headline, the rationale line included. */
  rest: string
}

const RATIONALE = /^Rationale: "(.*)"\s*$/ms

/** The one line a marker shows and what sits behind its expand. The
 * headline ends at the first period followed by a space or a line end. */
export function markerParts(text: string): MarkerParts {
  const flat = text.trim()
  const end = flat.search(/\.(?=\s|$)/)
  const headline = end >= 0 ? flat.slice(0, end + 1) : flat
  const rest = end >= 0 ? flat.slice(end + 1).trim() : ''
  const line = rest.split('\n').find((l) => l.startsWith('Rationale: "'))
  const rationale = line ? RATIONALE.exec(line)?.[1] ?? null : null
  return { headline, rationale, rest }
}

export const MARKER_TIP = { show: 'Show the full note', hide: 'Hide the full note' } as const
